import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit3, DollarSign, Trash2, Plus, Save, Check, XCircle, Banknote, FileX, Camera } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { statusConfig, getEffectiveStatus } from './invoicesConstants';
import { getAdjustmentBorderClass, getAdjustmentBadgeClass } from '../../../lib/statusHelpers';
import { LocationLine } from '../../../components/LocationLine';

const newUid = (seed) =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${seed}-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const InvoiceDetailDialog = ({
  open, onOpenChange, invoice, processing,
  onApprove, onReject, onApplyPayment, onVoidSingle, onInvoiceUpdated, onRefreshList,
}) => {
  const [editingItems, setEditingItems] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editAdminNotes, setEditAdminNotes] = useState('');
  const [savingItems, setSavingItems] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditingItems(false);
      setEditItems([]);
      setEditAdminNotes('');
    }
  }, [open]);

  const handleStartEditItems = () => {
    if (!invoice) return;
    const items = (invoice.items || []).map(item => ({
      _uid: newUid(item.product_id || 'item'),
      product_id: item.product_id || '',
      product_name: item.product_name || item.name || '',
      pack_size: item.pack_size || '',
      quantity: item.quantity || 0,
      unit_price: item.unit_price ?? item.price ?? 0,
      has_discount: item.has_discount || false,
      discount_percent: item.discount_percent || 0,
      discount_amount: item.discount_amount || 0,
      original_price: item.original_price || null,
      image_url: item.image_url || null,
    }));
    setEditItems(items);
    setEditAdminNotes('');
    setEditingItems(true);
  };

  const handleCancelEditItems = () => {
    setEditingItems(false);
    setEditItems([]);
    setEditAdminNotes('');
  };

  const handleEditItemChange = (index, field, value) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveEditItem = (index) => {
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddEditItem = () => {
    setEditItems(prev => [...prev, {
      _uid: newUid('new'),
      product_id: '',
      product_name: '',
      pack_size: '',
      quantity: 1,
      unit_price: 0,
      has_discount: false,
    }]);
  };

  const handleSaveItems = async () => {
    if (editItems.length === 0) {
      toast.error('Invoice must have at least one line item');
      return;
    }
    for (const item of editItems) {
      if (!item.product_name.trim()) {
        toast.error('All items must have a product name');
        return;
      }
    }
    setSavingItems(true);
    try {
      const payload = editItems.map(item => ({
        product_id: item.product_id || undefined,
        product_name: item.product_name,
        pack_size: item.pack_size || undefined,
        quantity: parseInt(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        has_discount: item.has_discount || false,
        discount_percent: item.discount_percent || 0,
        discount_amount: item.discount_amount || 0,
        original_price: item.original_price || undefined,
        image_url: item.image_url || undefined,
      }));
      const result = await adminService.updateInvoiceItems(invoice.id, payload, editAdminNotes);
      toast.success(result.message || 'Invoice updated');
      setEditingItems(false);
      setEditItems([]);
      const updated = await adminService.getInvoiceDetail(invoice.id);
      onInvoiceUpdated(updated);
      await onRefreshList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSavingItems(false);
    }
  };

  const editItemsTotal = editItems.reduce(
    (sum, item) => sum + (parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 0), 0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {invoice && (
          <>
            <DialogHeader>
              <DialogTitle>Invoice {invoice.invoice_number}</DialogTitle>
              <DialogDescription>{invoice.client_name} — {invoice.client_type_name || 'N/A'}</DialogDescription>
              <LocationLine
                name={invoice.location_name}
                source={invoice.location_name_source}
                clientName={invoice.client_name}
                testId={`invoice-detail-location-${invoice.id}`}
              />
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500 uppercase">Status</p><Badge className={statusConfig[getEffectiveStatus(invoice)]?.color}>{statusConfig[getEffectiveStatus(invoice)]?.label}</Badge></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500 uppercase">Amount</p><p className="font-bold text-xl">${(invoice.amount || 0).toFixed(2)}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500 uppercase">Invoice Date</p><p className="font-medium">{new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString()}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500 uppercase">Due Date</p><p className="font-medium">{new Date(invoice.due_date).toLocaleDateString()}</p></div>
                {invoice.order_date && (
                  <div className="p-3 bg-gray-50 rounded-lg" data-testid="admin-invoice-order-date">
                    <p className="text-xs text-gray-500 uppercase">Order Date</p>
                    <p className="font-medium">{new Date(invoice.order_date).toLocaleDateString()}</p>
                  </div>
                )}
                {invoice.delivery_date && (
                  <div className="p-3 bg-gray-50 rounded-lg" data-testid="admin-invoice-delivery-date">
                    <p className="text-xs text-gray-500 uppercase">Delivery Date</p>
                    <p className="font-medium">{new Date(invoice.delivery_date).toLocaleDateString()}</p>
                  </div>
                )}
                {invoice.order_number && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Order</p>
                    <p className="font-medium text-amber-600">{invoice.order_number}</p>
                    {invoice.order_id && (
                      <Link
                        to={`/admin/orders?order=${invoice.order_id}`}
                        className="text-xs text-amber-600 hover:text-amber-700 underline inline-flex items-center gap-1 mt-0.5"
                        data-testid="admin-invoice-proof-link"
                      >
                        <Camera className="h-3 w-3" /> Delivery proof
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* Pickup adjustment notice */}
              {invoice.has_pickup_adjustments && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="pickup-adjustment-notice">
                  Quantities were adjusted at pickup; this invoice reflects delivered quantities.
                </div>
              )}

              {/* Advance invoice provenance */}
              {invoice.billing_timing === 'advance' && (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-testid="admin-advance-invoice-notice">
                  <p className="font-medium">
                    Issued in advance
                    {invoice.parent_invoice_number ? ` (additional invoice for ${invoice.parent_invoice_number})` : ''}
                  </p>
                  {invoice.advance_invoice_reason && (
                    <p className="text-xs mt-0.5">Reason: {invoice.advance_invoice_reason}</p>
                  )}
                  {invoice.advance_invoiced_by_name && (
                    <p className="text-xs">
                      By {invoice.advance_invoiced_by_name}
                      {invoice.invoiced_at_order_status ? ` · order was ${invoice.invoiced_at_order_status.replace(/_/g, ' ')}` : ''}
                    </p>
                  )}
                  <p className="text-xs mt-0.5">
                    {invoice.revenue_recognized_at
                      ? `Reconciled at delivery on ${new Date(invoice.revenue_recognized_at).toLocaleDateString()}`
                      : 'Not yet delivered — will be reconciled against delivered quantities'}
                  </p>
                </div>
              )}

              {/* Credits raised against this invoice */}
              {invoice.credit_memos?.length > 0 && (
                <div className="space-y-2" data-testid="admin-invoice-credit-memos">
                  {invoice.credit_memos.map((cm) => {
                    const fromChange = cm.void_category === 'order_change_request';
                    return (
                      <div key={cm.id} className="p-3 rounded-lg bg-green-50 border border-green-200">
                        <p className="text-sm font-medium text-green-800">
                          Credit issued: ${Math.abs(cm.credit_amount || 0).toFixed(2)}
                          <span className="ml-2 text-xs font-normal">{cm.credit_memo_number}</span>
                        </p>
                        <p className="text-xs text-green-700 mt-0.5">
                          {cm.void_category_label} · by {cm.voided_by_email || 'system'}
                          {cm.voided_at ? ` · ${new Date(cm.voided_at).toLocaleString()}` : ''}
                        </p>
                        {(cm.lines || []).map((ln) => (
                          <p key={`${cm.id}-${ln.product_id}`} className="text-xs text-green-800 mt-0.5">
                            {ln.product_name}: {fromChange ? 'Ordered' : 'Invoiced'} {ln.invoiced_qty} → {fromChange ? 'Requested' : 'Delivered'} {ln.delivered_qty}
                            {ln.reason ? ` — ${ln.reason}` : ''}
                          </p>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Items */}
              {invoice.items?.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-gray-700">Line Items</p>
                    {!editingItems && !['void', 'cancelled'].includes(invoice.status) && (
                      <Button variant="outline" size="sm" onClick={handleStartEditItems} data-testid="edit-line-items-btn">
                        <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit Items
                      </Button>
                    )}
                  </div>

                  {editingItems ? (
                    <div className="space-y-3" data-testid="edit-items-form">
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-2">Product</th>
                              <th className="text-right p-2 w-20">Qty</th>
                              <th className="text-right p-2 w-28">Unit Price</th>
                              <th className="text-right p-2 w-24">Subtotal</th>
                              <th className="p-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {editItems.map((item, i) => (
                              <tr key={item._uid || i} className="border-t" data-testid={`edit-item-row-${i}`}>
                                <td className="p-2">
                                  <Input value={item.product_name} onChange={(e) => handleEditItemChange(i, 'product_name', e.target.value)} className="h-8 text-sm" placeholder="Product name" data-testid={`edit-item-name-${i}`} />
                                </td>
                                <td className="p-2">
                                  <Input type="number" min="0" value={item.quantity} onChange={(e) => handleEditItemChange(i, 'quantity', e.target.value)} className="h-8 text-sm text-right w-20" data-testid={`edit-item-qty-${i}`} />
                                </td>
                                <td className="p-2">
                                  <div className="relative">
                                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                                    <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => handleEditItemChange(i, 'unit_price', e.target.value)} className="h-8 text-sm text-right pl-6 w-28" data-testid={`edit-item-price-${i}`} />
                                  </div>
                                </td>
                                <td className="p-2 text-right font-medium">
                                  ${((parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                                </td>
                                <td className="p-2">
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleRemoveEditItem(i)} data-testid={`remove-edit-item-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-between items-center">
                        <Button variant="outline" size="sm" onClick={handleAddEditItem} data-testid="add-edit-item-btn">
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Line Item
                        </Button>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">New Total</p>
                          <p className="text-lg font-bold text-amber-600" data-testid="edit-items-total">${editItemsTotal.toFixed(2)}</p>
                          {invoice.amount !== editItemsTotal && (
                            <p className="text-xs text-gray-400">was ${(invoice.amount || 0).toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Admin Notes (reason for edit)</Label>
                        <Textarea value={editAdminNotes} onChange={(e) => setEditAdminNotes(e.target.value)} rows={2} placeholder="e.g., Client reported damaged goods not covered by guaranteed returns..." className="mt-1 text-sm" data-testid="edit-admin-notes" />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={handleCancelEditItems} data-testid="cancel-edit-items-btn">Cancel</Button>
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={handleSaveItems} disabled={savingItems || editItems.length === 0} data-testid="save-edit-items-btn">
                          <Save className="h-3.5 w-3.5 mr-1" /> {savingItems ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr><th className="text-left p-2">Product</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit Price</th><th className="text-right p-2">Subtotal</th></tr></thead>
                        <tbody>{invoice.items.map((item, i) => (
                          <tr key={item.product_id ? `${item.product_id}-${i}` : i} className="border-t" data-testid={`line-item-${i}`}>
                            <td className="p-2">
                              {item.product_name || item.name}
                              {item.pickup_adjusted && (
                                <div className="mt-0.5" data-testid={`pickup-adjusted-${i}`}>
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">Adjusted at pickup</Badge>
                                  <span className="text-xs text-amber-700 ml-1.5">Ordered {item.ordered_qty} → Delivered {item.delivered_qty}</span>
                                </div>
                              )}
                            </td>
                            <td className="text-right p-2">{item.quantity}{item.original_quantity && item.original_quantity !== item.quantity && <span className="text-xs text-red-400 line-through ml-1">{item.original_quantity}</span>}</td>
                            <td className="text-right p-2">${(item.unit_price ?? item.price ?? 0).toFixed(2)}</td>
                            <td className="text-right p-2">${(item.subtotal || 0).toFixed(2)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Edit History */}
              {invoice.admin_edit_history?.length > 0 && !editingItems && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Edit History</p>
                  <div className="space-y-1.5">
                    {invoice.admin_edit_history.map((entry, i) => (
                      <div key={`${entry.edited_at}-${entry.edited_by_email || i}`} className="p-2 bg-gray-50 rounded text-xs text-gray-600 border" data-testid={`edit-history-${i}`}>
                        <div className="flex justify-between">
                          <span className="font-medium">{entry.edited_by_email}</span>
                          <span className="text-gray-400">{new Date(entry.edited_at).toLocaleString()}</span>
                        </div>
                        <p>${entry.previous_amount?.toFixed(2)} → ${entry.new_amount?.toFixed(2)} ({entry.previous_item_count} → {entry.new_item_count} items)</p>
                        {entry.admin_notes && <p className="text-gray-500 italic mt-0.5">{entry.admin_notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Adjustment Requests */}
              {invoice.adjustment_requests?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Adjustment Requests</p>
                  {invoice.adjustment_requests.map((adj, i) => (
                    <div key={adj.id || `${adj.requested_at}-${i}`} className={`p-4 rounded-lg mb-2 border-l-4 ${getAdjustmentBorderClass(adj.status)}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge className={getAdjustmentBadgeClass(adj.status)}>{adj.status}</Badge>
                          <p className="text-sm mt-2"><span className="text-gray-500">Amount:</span> ${adj.original_amount?.toFixed(2)} → ${adj.requested_amount?.toFixed(2)} <span className="text-red-600 text-xs">(-${adj.adjustment_amount?.toFixed(2)})</span></p>
                          <p className="text-sm"><span className="text-gray-500">Reason:</span> {adj.reason}</p>
                          <p className="text-xs text-gray-400 mt-1">Submitted: {new Date(adj.requested_at).toLocaleString()}</p>
                          {adj.rejection_reason && <p className="text-sm text-red-600 mt-1">Rejection: {adj.rejection_reason}</p>}
                        </div>
                        {adj.status === 'pending' && (
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApprove(invoice.id)} disabled={processing}><Check className="h-4 w-4 mr-1" />Approve</Button>
                            <Button size="sm" variant="destructive" onClick={onReject} disabled={processing}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                          </div>
                        )}
                      </div>
                      {adj.quantity_changes?.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs font-medium text-gray-500 mb-1">Quantity Changes:</p>
                          {adj.quantity_changes.map((c) => (
                            <p key={`${c.product_name}-${c.original_qty}-${c.new_qty}`} className="text-xs text-gray-600">{c.product_name}: {c.original_qty} → {c.new_qty} <span className="text-red-500">(-{c.original_qty - c.new_qty})</span></p>
                          ))}
                        </div>
                      )}
                      {adj.photo_urls?.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs font-medium text-gray-500 mb-1">Photos:</p>
                          <div className="flex gap-2">{adj.photo_urls.map((url) => <img key={url} src={url} alt="Evidence" className="h-20 w-20 object-cover rounded border" />)}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Manual Payments History */}
              {invoice.manual_payments?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Manual Payments</p>
                  <div className="space-y-2">
                    {invoice.manual_payments.map((mp, i) => (
                      <div key={mp.id || `${mp.applied_at}-${mp.amount}-${i}`} className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-green-800">${mp.amount?.toFixed(2)} — {mp.payment_type_label || mp.payment_type}</span>
                          <span className="text-xs text-gray-500">{new Date(mp.applied_at).toLocaleString()}</span>
                        </div>
                        {mp.comments && <p className="text-gray-600 text-xs mt-1">{mp.comments}</p>}
                        <p className="text-xs text-gray-400 mt-1">Recorded by: {mp.applied_by_email}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm flex justify-between">
                    <span className="text-gray-500">Total Paid</span>
                    <span className="font-bold">${(invoice.amount_paid || 0).toFixed(2)} / ${(invoice.amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Payment Terms:</span> {invoice.payment_terms || 'Net 30'}</div>
                <div><span className="text-gray-500">Auto-Pay:</span> {invoice.auto_pay_enabled ? 'Enabled' : 'Disabled'}</div>
                <div><span className="text-gray-500">Guaranteed Returns:</span> {invoice.has_guaranteed_returns ? 'Yes' : 'No'}</div>
                {invoice.adjustment_window_ends && <div><span className="text-gray-500">Adj. Window:</span> Until {new Date(invoice.adjustment_window_ends).toLocaleDateString()}</div>}
              </div>

              {/* Action Buttons */}
              {!['paid', 'cancelled', 'void'].includes(invoice.status) && (
                <div className="flex gap-2 pt-2">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => onApplyPayment(invoice)} data-testid="detail-apply-payment-btn">
                    <Banknote className="h-4 w-4 mr-2" />
                    Apply Payment
                  </Button>
                  <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={onVoidSingle} data-testid="detail-void-btn">
                    <FileX className="h-4 w-4 mr-2" />
                    Void Invoice
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
